<script setup lang="ts">
import type { Address } from "@/components/controls/address/addresses";
import { ref, watch } from "vue";
import { makeRef } from "@/components/composables/componentTools";
import debounce from "lodash-es/debounce";
import type { LoqateSearchResponse } from "@/components/controls/address/loqateService";
import { loqateService } from "@/components/controls/address/loqateService";
import BmControl from "@/components/controls/input/BmControl.vue";

/**
 * A control that encapsulates address entry - and an optional business name.
 *
 * Gives the user an edit option that invokes a dialog to capture new address
 * information.
 */

const emit = defineEmits<{
  (event: "failed-to-retrieve-address"): void;
  (event: "address-retrieved", address: Address): void;
  (event: "retrieving-address"): void;
}>();

const addressInput = ref<HTMLElement | null>(null);
const isLoading = ref(false);
const isRetrieving = ref(false);

// Search term input by user, what is used to refine address results
// These are pulled out separately so we can clear them as we search to prevent double searches
const userSearchTerm = ref("");
const currentSearchContainerId = ref("");

const selectedAddressSearchResult = ref<LoqateSearchResponse>();
const addressSearchResults = makeRef<readonly LoqateSearchResponse[]>([]);

const searchForAddresses = debounce(async () => {
  try {
    if (!userSearchTerm.value) {
      return;
    }

    const results = await loqateService.searchForAddress({
      Text: userSearchTerm.value,
      Container: currentSearchContainerId.value,
    });

    if (!results) {
      userSearchTerm.value = "";
      emit("failed-to-retrieve-address");
      return;
    }

    addressSearchResults.value = results ?? [];
    selectedAddressSearchResult.value = undefined;
  } finally {
    currentSearchContainerId.value = "";
    isLoading.value = false;
  }
}, 750);

const retrieveAddress = async () => {
  try {
    emit("retrieving-address");
    isRetrieving.value = true;
    let result = await loqateService.resolveLoqateId({
      Id: currentSearchContainerId.value,
    });

    // Couldn't find the result, so open the dialog and clear the term so it doesn't look like it worked
    if (!result) {
      userSearchTerm.value = "";
      emit("failed-to-retrieve-address");
      return;
    }

    emit("address-retrieved", result);
  } finally {
    currentSearchContainerId.value = "";
    isLoading.value = false;
    isRetrieving.value = false;
  }
};

const updateSearchParams = (result: LoqateSearchResponse) => {
  if (result.Id === currentSearchContainerId.value) {
    return;
  }

  isLoading.value = true;
  currentSearchContainerId.value = result.Id;
  userSearchTerm.value = result.Text;
  selectedAddressSearchResult.value = result;

  if (result.Type === "Container") {
    searchForAddresses();
  } else {
    retrieveAddress();
    addressInput.value?.blur();
  }
};

// Clicking an item in the dropdown will update the search term
watch(userSearchTerm, (newVal, oldVal) => {
  // If they're the same, or the first setup of our watcher
  if (newVal === oldVal || !newVal) {
    return;
  }

  // If we have a container, we've clicked an item so let the update method handle
  if (currentSearchContainerId.value) {
    return;
  }

  isLoading.value = true;
  searchForAddresses();
});
</script>

<template>
  <bm-control v-bind="$attrs">
    <template #default="{ dataQa }">
      <v-autocomplete
        ref="addressInput"
        :placeholder="$m.smart_address_start_typing"
        :value="selectedAddressSearchResult"
        :search-input.sync="userSearchTerm"
        :items="addressSearchResults"
        :loading="isLoading"
        :data-qa="dataQa"
        :disabled="isRetrieving"
        item-value="Id"
        item-text="Text"
        return-object
        autocomplete="none"
        no-filter
        hide-no-data
        v-bind="$attrs"
      >
        <template #item="data">
          <v-list-item
            v-bind="data.attrs"
            @click="updateSearchParams(data.item)"
          >
            <v-list-item-content>
              <v-list-item-title>
                {{ data.item.Text }}
                <v-chip
                  v-if="data.item.Description"
                  color="infoLight"
                  class="ml-2"
                  >{{ data.item.Description.replace(" - ", "") }}
                </v-chip>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
        <template #append-outer>
          <slot name="append-outer" />
        </template>
      </v-autocomplete>
    </template>
  </bm-control>
</template>
